import PropTypes from 'prop-types';
import { Component } from 'react';
import { displayAds } from 'src/client/displayAds';
import { buildContentTargeting } from 'src/client/displayAds/buildContentTargeting';
import { mintSingleton } from 'src/client/mint';
import { player } from 'src/client/player';
import { storeSingleton } from 'src/client/store/storeSingleton';
import { REDIRECT_CATEGORY_ID } from 'src/common/constants/ads/location';
import { selectIsNowPlayingDialogOpen } from 'src/common/selectors/dialog';
import { isCategory, isProfile } from '../../utils/guideItemTypes';
import isServer from '../../utils/isServer';

function getMatchUrl(props) {
  return props.routeProps?.matchUrl ?? props.match?.url;
}

/**
 * The legacy path reads its content key-values from `profiles[selectedGuideId]`, where
 * `selectedGuideId` is this same route guide context — so deriving them here, rather than in
 * Profile, covers every screen it covers. Returns `{}` off a guide route, which clears the previous
 * page's keys.
 */
function getRouteContentTargeting(props) {
  const guideId = props.routeProps?.guideContext?.guideId;

  if (!guideId || isCategory(guideId)) {
    return {};
  }

  return buildContentTargeting(storeSingleton.state.profiles?.[guideId]);
}

/**
 * The route the most recent screen reported, shared by every wrapped connector. Navigating to a
 * route served by a different connector (e.g. `/radio/…` → `/podcasts/`) unmounts one wrapper and
 * mounts another, so no `componentDidUpdate` sees the route change, while the layout's display ads
 * stay mounted across it. The new wrapper compares against this instead.
 */
let lastScreenMatchUrl;

export default function withAdScreenRequest(getScreenName) {
  return (BaseComponent) => {
    class WithScreenAdRequest extends Component {
      static propTypes = {
        match: PropTypes.object.isRequired,
        routeProps: PropTypes.shape({
          matchUrl: PropTypes.string,
        }),
      };

      #unsubscribeStore;

      #isNowPlayingDialogOpen = false;

      #profileCategoryId = null;

      constructor(props) {
        super(props);
        if (!isServer()) {
          const isNavigation =
            lastScreenMatchUrl !== undefined && getMatchUrl(props) !== lastScreenMatchUrl;

          this.updateCurrentScreen({ refreshDisplayAds: isNavigation });
        }
      }

      /**
       * The now-playing screen is not a route: tuning dispatches `openNowPlayingDialog()` from
       * `player.#playSideEffects`, so `componentDidUpdate` never fires and the screen key kept the
       * value the route last pushed. `screen=nowPlaying` was therefore never emitted at all, and a
       * later navigation *while* the dialog was open would have labelled the next route with it.
       *
       * Watched on the store for the same reason `screenViewTracker` does it (which is why the
       * analytics screen has always been right where this one was not): the dialog is redux state,
       * not routing.
       */
      componentDidMount() {
        if (isServer()) {
          return;
        }

        this.#isNowPlayingDialogOpen = selectIsNowPlayingDialogOpen(storeSingleton.state);

        this.#unsubscribeStore = storeSingleton.subscribe(() => {
          const isNowPlayingDialogOpen = selectIsNowPlayingDialogOpen(storeSingleton.state);

          if (isNowPlayingDialogOpen === this.#isNowPlayingDialogOpen) {
            return;
          }

          this.#isNowPlayingDialogOpen = isNowPlayingDialogOpen;
          // Deliberately no refresh: the route has not changed, so this only needs to be true of the
          // NEXT request, and re-requesting live slots to change one key-value costs an auction per
          // slot. Route changes still refresh, as before.
          void this.updateCurrentScreen();
        });
      }

      componentWillUnmount() {
        this.#unsubscribeStore?.();
      }

      /**
       * A profile opened from a category page keeps that category (the link carries it in router
       * state, see `SponsoredRollupProvider`) so category sponsorships follow the user onto it, and
       * so do the profiles reached from that one. Any other screen drops it, and so does a route
       * served by another connector, since that remounts this wrapper. Every screen update rewrites
       * `categoryId`, so the carried value is held here rather than written once.
       */
      #resolveCategoryId() {
        const guideId = this.props.routeProps?.guideContext?.guideId;

        if (isCategory(guideId)) {
          this.#profileCategoryId = null;
          return guideId;
        }

        if (!isProfile(guideId)) {
          this.#profileCategoryId = null;
          return null;
        }

        const redirectCategoryId = this.props.location?.state?.[REDIRECT_CATEGORY_ID];

        if (isCategory(redirectCategoryId)) {
          this.#profileCategoryId = redirectCategoryId;
        }

        return this.#profileCategoryId;
      }

      async updateCurrentScreen({ refreshDisplayAds = false } = {}) {
        lastScreenMatchUrl = getMatchUrl(this.props);
        const isNowPlayingDialogOpen = selectIsNowPlayingDialogOpen(storeSingleton.state);
        const screenName = isNowPlayingDialogOpen ? 'nowPlaying' : getScreenName(this.props);

        await displayAds.setPageTargeting({
          screen: screenName,
          categoryId: this.#resolveCategoryId(),
        });
        await displayAds.setContentTargeting(getRouteContentTargeting(this.props));
        mintSingleton.updateState('currentScreen', screenName);
        player.setAppSessionData({ currentScreen: screenName });

        if (refreshDisplayAds && displayAds.isReady) {
          await displayAds.refresh();
        }
      }

      componentDidUpdate(prevProps) {
        const prevMatchUrl = getMatchUrl(prevProps);
        const matchUrl = getMatchUrl(this.props);
        const routeChanged = prevMatchUrl !== matchUrl;
        const paramsChanged = prevProps.match?.params !== this.props.match?.params;

        if (routeChanged || paramsChanged) {
          void this.updateCurrentScreen({ refreshDisplayAds: routeChanged });
        }
      }

      render() {
        return <BaseComponent {...this.props} />;
      }
    }

    return WithScreenAdRequest;
  };
}
